import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getProjectDashboard,
  getIssueDetail,
  getIssueComments,
  addIssueComment,
  sendTeamMemberDM,
  ProjectDashboard,
  IssueDetailResponse,
  IssueComment,
} from '@/api/project-dashboard.api';

/**
 * Hook to get project dashboard
 */
export function useProjectDashboard(
  projectId: string | undefined,
  options?: {
    issueState?: 'opened' | 'closed' | 'all';
    mrState?: 'opened' | 'closed' | 'merged' | 'all';
    staleThresholdDays?: number;
  }
) {
  return useQuery({
    queryKey: ['project-dashboard', projectId, options],
    queryFn: () => getProjectDashboard(projectId!, options),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Hook to get issue detail
 */
export function useIssueDetail(
  projectId: string | undefined,
  repoId: string | undefined,
  issueIid: number | undefined
) {
  return useQuery({
    queryKey: ['issue-detail', projectId, repoId, issueIid],
    queryFn: () => getIssueDetail(projectId!, repoId!, issueIid!),
    enabled: !!projectId && !!repoId && !!issueIid,
  });
}

/**
 * Hook to get issue comments from GitLab
 */
export function useIssueComments(
  projectId: string | undefined,
  repoId: string | undefined,
  issueIid: number | undefined,
  limit: number = 20
) {
  return useQuery({
    queryKey: ['issue-comments', projectId, repoId, issueIid, limit],
    queryFn: () => getIssueComments(projectId!, repoId!, issueIid!, limit),
    enabled: !!projectId && !!repoId && !!issueIid,
    staleTime: 1000 * 60 * 1, // 1 minute
  });
}

/**
 * Hook to add comment to issue
 */
export function useAddIssueComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      repoId,
      issueIid,
      body,
    }: {
      projectId: string;
      repoId: string;
      issueIid: number;
      body: string;
    }) => addIssueComment(projectId, repoId, issueIid, body),
    onSuccess: (_data, variables) => {
      // Invalidate both issue detail and comments
      queryClient.invalidateQueries({
        queryKey: ['issue-detail', variables.projectId, variables.repoId, variables.issueIid],
      });
      queryClient.invalidateQueries({
        queryKey: ['issue-comments', variables.projectId, variables.repoId, variables.issueIid],
      });
    },
  });
}

/**
 * Hook to send DM to team member
 */
export function useSendTeamMemberDM() {
  return useMutation({
    mutationFn: ({
      projectId,
      userId,
      content,
      context,
    }: {
      projectId: string;
      userId: string;
      content: string;
      context?: { issueTitle?: string };
    }) => sendTeamMemberDM(projectId, userId, content, context),
  });
}

export type { ProjectDashboard, IssueDetailResponse, IssueComment };
